/**
 * @fileoverview The 12-step guided-tour script for the document builder canvas (Phase 5, Workstream K2).
 * @module ui-admin/components/DocumentCanvasBuilder/builderTourSteps
 *
 * Responsibilities: - Define the ordered list of `TourStep`s that walk a first-time user through
 *                     the builder, each targeting a real on-page element via a `[data-tour="…"]`
 *                     selector so the spotlight lands on the live UI.
 * Dependencies:     - `TourStep` from the help types.
 * Architecture:     - Pure static data bundled with the app (no API/Dexie, no mock data). Co-located
 *                     with the shared `DocumentCanvasBuilder` so both tenant-web and system-web reuse
 *                     the same tour; targets that live on the host page (left panel, preview pane,
 *                     save/publish) carry matching `data-tour` attributes in those pages.
 * Notes:            - GuidedTour centres the tooltip when a `targetSelector` is empty or missing, so
 *                     the tour still runs gracefully if a host page omits an optional target.
 */

import type { TourStep } from '../../help/types';

/** Stable `data-tour` attribute values used as the tour's CSS selector targets. */
export const TOUR_TARGET = {
  leftPanel: 'left-panel',
  pageSetup: 'page-setup',
  surfaceTabs: 'surface-tabs',
  canvas: 'canvas',
  addComponent: 'add-component',
  dynamicBand: 'dynamic-band',
  bandMenu: 'band-menu',
  bandHelp: 'band-help',
  preview: 'preview',
  previewHandle: 'preview-handle',
  savePublish: 'save-publish',
} as const;

const sel = (target: string) => `[data-tour="${target}"]`;

/**
 * @contract BUILDER_TOUR_STEPS
 * Purpose: Ordered 12-step walkthrough of the document builder canvas.
 * Rules: Step order is the tour order; every non-welcome step targets a real element selector.
 */
export const BUILDER_TOUR_STEPS: TourStep[] = [
  {
    targetSelector: '',
    title: 'Welcome to the Document Builder',
    body: 'Here\u2019s the lay of the land. The builder has three zones: a configuration panel, the canvas where you build the page, and a live preview. Let\u2019s walk through them.',
    placement: 'center',
  },
  {
    targetSelector: sel(TOUR_TARGET.leftPanel),
    title: 'Configuration panel',
    body: 'This panel controls your document\u2019s page setup, header, section list, and branding.',
    placement: 'right',
  },
  {
    targetSelector: sel(TOUR_TARGET.pageSetup),
    title: 'Page setup',
    body: 'Expand Page Setup to choose paper size, orientation, margins, and an optional border.',
    placement: 'right',
  },
  {
    targetSelector: sel(TOUR_TARGET.surfaceTabs),
    title: 'Body / Cover Page tabs',
    body: 'Use these tabs to switch between building the main document body and adding a cover page.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(TOUR_TARGET.canvas),
    title: 'The canvas',
    body: 'This is your page. You build it by adding sections called bands \u2014 drag them into the order you want.',
    placement: 'left',
  },
  {
    targetSelector: sel(TOUR_TARGET.addComponent),
    title: 'Add a component',
    body: 'Click here to open the component palette and add a new section to your document.',
    placement: 'bottom',
  },
  {
    targetSelector: sel(TOUR_TARGET.dynamicBand),
    title: 'Dynamic sections',
    body: 'Sections marked DYNAMIC expand with real inspection data at render time. Use \u201cPreview with sample data\u201d to see how.',
    placement: 'left',
  },
  {
    targetSelector: sel(TOUR_TARGET.bandMenu),
    title: 'Band menu',
    body: 'Every band has a menu for moving, duplicating, or deleting it.',
    placement: 'left',
  },
  {
    targetSelector: sel(TOUR_TARGET.bandHelp),
    title: 'Per-section help',
    body: 'Click the ? on any band for detailed help on that specific section.',
    placement: 'left',
  },
  {
    targetSelector: sel(TOUR_TARGET.preview),
    title: 'Live preview',
    body: 'The live preview shows exactly what your PDF will look like \u2014 the same engine renders both.',
    placement: 'left',
  },
  {
    targetSelector: sel(TOUR_TARGET.previewHandle),
    title: 'Resize the preview',
    body: 'Drag this handle to resize the preview, or drag it all the way for a full-screen view.',
    placement: 'left',
  },
  {
    targetSelector: sel(TOUR_TARGET.savePublish),
    title: 'Save & Publish',
    body: 'Save keeps your draft; Publish makes the template available for generating real documents.',
    placement: 'bottom',
  },
];
